// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IButtonStyles } from "@fluentui/react";
import type { IContextualMenuProps } from "@fluentui/react/lib/ContextualMenu";
import type { IFocusZoneProps } from "@fluentui/react/lib/FocusZone";
import type { IIconProps } from "@fluentui/react/lib/Icon";
import type { IKeytipProps } from "@fluentui/react/lib/Keytip";
import type { IStyle, ITheme } from "@fluentui/react/lib/Styling";
import type { IBaseProps } from "@fluentui/react/lib/Utilities";

import type {
  AppColors,
  AppSharedTooltipProps,
  IMenuItemClassNames,
} from "@1js/acui-common";

import type {
  IPivotHeaderStyles,
  PivotHeaderExecutePayload,
} from "../PivotHeader";

export enum PivotDirection {
  Horizontal,
  Vertical,
}

/* eslint-disable @typescript-eslint/naming-convention */
// tslint:disable-next-line:interface-name
export interface IPivotProps extends IBaseProps, AppSharedTooltipProps {
  /* eslint-enable @typescript-eslint/naming-convention */
  /**
   * The label for this Pivot
   */
  label?: string;

  /**
   * The label for the overflow menu launcher of this Pivot
   */
  overflowMenuLauncherLabel: string;

  /**
   * The list of Pivot headers that appear at the top level
   */
  topLevelItems: IPivotHeader[];

  /**
   * The list of Pivot headers that appear in an overflow menu
   */
  overflowItems?: IPivotHeader[];

  /**
   * The unique identifier of the currently selected Pivot header. This component does not handle tab switching, it is driven by the parent.
   */
  selectedPivotId?: string;

  /**
   * Callback to invoke when Pivot header is clicked. The callback takes the id of the
   * selected item, and the payload if any.
   */
  onPivotHeaderExecute?: (
    id: string,
    payload?: PivotHeaderExecutePayload
  ) => void;

  /**
   * Callback to invoke when Pivot's Overflow Button is clicked.
   */
  onOverflowButtonExecute?: () => void;

  /**
   * Callback for mouse enter on a Pivot header
   * @param id - id of the pivot header where the event fired
   */
  onPivotHeaderMouseEnter?: (id: string) => void;

  /**
   * Callback for mouse leave on a Pivot header
   * @param id - id of the pivot header where the event fired
   */
  onPivotHeaderMouseLeave?: (id: string) => void;

  /**
   * Callback for mouse down on a Pivot header
   * @param id - id of the pivot header where the event fired
   */
  onPivotHeaderMouseDown?: (id: string) => void;

  /**
   * Callback for mouse up on a Pivot header
   * @param id - id of the pivot header where the event fired
   */
  onPivotHeaderMouseUp?: (id: string) => void;

  /**
   * Callback for mouse click on a Pivot header
   * @param id - id of the pivot header where the event fired
   */
  onPivotHeaderClick?: (id: string) => void;

  /**
   * Callback for blur on a Pivot header
   * @param id - id of the pivot header where the event fired
   */
  onPivotHeaderBlur?: (id: string) => void;

  /**
   * Callback on focus of the Pivot header
   * @param id - id of the pivot header where the event fired
   */
  onPivotHeaderFocus?: (id: string) => void;

  /**
   * Custom styling for individual elements within the Pivot DOM.
   */
  styles?: IPivotStyles;

  /**
   * Properties for the visuals of the menu that launches when the user clicks on the overflow button.
   * Note that some properties, such as 'items', will be explicitly set by the Pivot, so setting them here
   * will have no impact. However, properties affecting visuals should be respected.
   */
  menuProps?: Partial<IContextualMenuProps>;

  /** The properties for the icon in the button that launches the overflow menu */
  menuIconProps?: IIconProps;

  /**
   * Provides the app-specific colors to the control
   */
  appColors?: AppColors;

  /**
   * Optional keytip for the overflow button in this Pivot
   */
  keytipProps?: IKeytipProps;

  /**
   * True if the selected pivot header should render the placeholder selected indicator
   */
  displayTabIndicatorPlaceholder?: boolean;

  /**
   * Optional props for the FocusZone created by the inner OverflowSet
   */
  focusZoneProps?: IFocusZoneProps;

  /**
   * Direction of the Pivot
   * Defaults to PivotDirection.Horizontal if not specified
   */
  direction?: PivotDirection;

  /**
   * Optional prop that gets passed on to the PivotHeader contained in this Pivot
   * that is used as a type identifier for testing purposes.
   */
  automationType?: string;

  /**
   * Temporary prop, T/F if the new styles should be used when the flight is on
   * This is because Ribbon components like TabList, Pivot, etc are used outside of the Ribbon
   */
  useNextStyles?: boolean;

  /**
   * If true we will hide the indicators of which tab is currently selected.
   */
  hideSelectedTabIndicators?: boolean;
}
/* eslint-disable @typescript-eslint/naming-convention */
// tslint:disable-next-line:interface-name
export interface IPivotHeader extends AppSharedTooltipProps {
  /* eslint-enable @typescript-eslint/naming-convention */
  /**
   * A unique identifier for the Pivot header.
   */
  id: string;

  /**
   * The text to display for this pivot item.
   */
  title: string;

  /**
   * Optional icon to display for this pivot item
   */
  icon?: string;

  /**
   * If true, hide the label for this pivot item
   */
  hideLabel?: boolean;

  /**
   * Custom styling for individual elements within the Pivot DOM.
   */
  styles?: Partial<IPivotHeaderStyles>;

  /**
   * Method to provide the styles for the PivotHeader when it appears in the overflow menu.
   */
  getMenuItemClassNames?: (
    theme: ITheme,
    disabled: boolean
  ) => IMenuItemClassNames;

  /**
   * A unique identifier for the UI that this pivot header is associated with
   * (e.g. the UI that appears when the PivotHeader is executed... menu, menubar, etc...).
   * Note: This value should be set as the id of the associated UI element
   */
  ariaControls?: string;

  /**
   * The 1-based index in the set of Pivot Headers to which this PivotHeader instance belongs
   */
  ariaPosInSet?: number;

  /**
   * The size of the set of Pivot Headers to which this PivotHeader instance belongs
   */
  ariaSetSize?: number;

  /**
   * Optional aria-label for the PivotHeader
   */
  ariaLabel?: string;

  /**
   * Set this to true if the tab header should render in a disabled state and not respond to click or keyboard events.
   */
  disabled?: boolean;

  /**
   * Optional keytip for this IPivotHeader
   */
  keytipProps?: IKeytipProps;
}

// tslint:disable-next-line:interface-name
export interface IPivotStyles {
  /**
   * Style for the root Pivot element
   */
  root?: IStyle;

  /**
   * Styles for the button that launches menu containing overflow Pivot Headers.
   */
  overflowButton?: IButtonStyles;

  /**
   * Styles for the pivot headers
   */
  pivotHeaderStyles?: IPivotHeaderStyles;

  /**
   * Styles for contextual pivot headers
   */
  contextualPivotHeaderStyles?: IPivotHeaderStyles;
}
